/**
 * Set de iconos del panel.
 *
 * Trazo sobre lienzo de 24×24, sin relleno: heredan `currentColor` y el grosor
 * se mantiene óptico a cualquier tamaño. Se dibujan inline para no depender de
 * una fuente de iconos ni de un CDN (el panel no carga recursos externos).
 *
 * Cada icono es una lista de primitivas para poder pintarlas con un `@for`
 * genérico en vez de tener un `@switch` de cincuenta ramas en la plantilla.
 */

export interface IconPrimitive {
  kind: 'path' | 'circle' | 'line' | 'rect' | 'polyline';
  d?: string;
  cx?: number;
  cy?: number;
  r?: number;
  x1?: number;
  y1?: number;
  x2?: number;
  y2?: number;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  rx?: number;
  points?: string;
  /** El único caso con relleno: la estrella llena de las calificaciones. */
  filled?: boolean;
}

const p = (d: string): IconPrimitive => ({ kind: 'path', d });
const c = (cx: number, cy: number, r: number): IconPrimitive => ({ kind: 'circle', cx, cy, r });
const l = (x1: number, y1: number, x2: number, y2: number): IconPrimitive => ({
  kind: 'line',
  x1,
  y1,
  x2,
  y2,
});
const r = (x: number, y: number, width: number, height: number, rx = 2): IconPrimitive => ({
  kind: 'rect',
  x,
  y,
  width,
  height,
  rx,
});
const pl = (points: string): IconPrimitive => ({ kind: 'polyline', points });

export const ICONS: Record<string, IconPrimitive[]> = {
  // ---------- Navegación y secciones ----------
  dashboard: [r(3, 3, 7, 9), r(14, 3, 7, 5), r(14, 12, 7, 9), r(3, 16, 7, 5)],
  users: [
    p('M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2'),
    c(9, 7, 4),
    p('M22 21v-2a4 4 0 0 0-3-3.87'),
    p('M16 3.13a4 4 0 0 1 0 7.75'),
  ],
  user: [p('M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2'), c(12, 7, 4)],
  store: [
    p('M3 9.5 4.5 4h15L21 9.5'),
    p('M3 9.5a3 3 0 0 0 6 0 3 3 0 0 0 6 0 3 3 0 0 0 6 0'),
    p('M5 12v8h14v-8'),
    p('M9 20v-5h6v5'),
  ],
  car: [
    p('M5 13 6.6 7.4A2 2 0 0 1 8.5 6h7a2 2 0 0 1 1.9 1.4L19 13'),
    p('M3 13h18v5H3z'),
    c(7, 18, 1.6),
    c(17, 18, 1.6),
  ],
  package: [
    p('M12 2 3 6.5v11L12 22l9-4.5v-11z'),
    pl('3 6.5 12 11 21 6.5'),
    l(12, 11, 12, 22),
    p('M7.5 4.2 16.5 8.8'),
  ],
  'credit-card': [r(2, 5, 20, 14, 2), l(2, 10, 22, 10), l(6, 15, 10, 15)],
  headset: [
    p('M4 14v-2a8 8 0 0 1 16 0v2'),
    p('M4 14h2a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1z'),
    p('M20 14h-2a1 1 0 0 0-1 1v3a1 1 0 0 0 1 1h1a1 1 0 0 0 1-1z'),
    p('M20 19v.5a2.5 2.5 0 0 1-2.5 2.5H13'),
  ],
  undo: [pl('9 14 4 9 9 4'), p('M20 20v-5a6 6 0 0 0-6-6H4')],
  shield: [p('M12 2 4 5.5v6c0 4.5 3.2 8.6 8 10.5 4.8-1.9 8-6 8-10.5v-6z')],
  'shield-alert': [
    p('M12 2 4 5.5v6c0 4.5 3.2 8.6 8 10.5 4.8-1.9 8-6 8-10.5v-6z'),
    l(12, 8, 12, 12.5),
    l(12, 15.5, 12, 15.6),
  ],
  star: [p('m12 3 2.9 5.9 6.5.9-4.7 4.6 1.1 6.5-5.8-3.1-5.8 3.1 1.1-6.5L3.6 9.8l6.5-.9z')],
  'star-filled': [
    { ...p('m12 3 2.9 5.9 6.5.9-4.7 4.6 1.1 6.5-5.8-3.1-5.8 3.1 1.1-6.5L3.6 9.8l6.5-.9z'), filled: true },
  ],
  image: [r(3, 4, 18, 16, 2), c(8.5, 9.5, 1.8), pl('3 17 8.5 12 13 16 16.5 13 21 17')],
  wrench: [
    p('M14.5 3.5a5 5 0 0 0 6 6.4l-9.6 9.6a2.6 2.6 0 0 1-3.7-3.7l9.6-9.6a5 5 0 0 0-2.3-2.7z'),
  ],
  bell: [p('M18 9a6 6 0 0 0-12 0c0 5-2 6-2 6h16s-2-1-2-6'), p('M10.5 20a2 2 0 0 0 3 0')],
  'file-text': [
    p('M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7z'),
    pl('14 2 14 7 19 7'),
    l(9, 13, 15, 13),
    l(9, 17, 13, 17),
  ],
  receipt: [
    p('M5 2v20l2.5-1.6L10 22l2-1.6L14 22l2.5-1.6L19 22V2z'),
    l(9, 8, 15, 8),
    l(9, 12, 15, 12),
  ],
  search: [c(11, 11, 7), l(16.2, 16.2, 21, 21)],
  settings: [
    c(12, 12, 3),
    p(
      'M19.4 15a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-2.7 1.1V21a2 2 0 1 1-4 0v-.1A1.6 1.6 0 0 0 7 19.4l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.6 1.6 0 0 0-1.1-2.7H3a2 2 0 1 1 0-4h.1A1.6 1.6 0 0 0 4.6 7l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.6 1.6 0 0 0 2.7-1.1V3a2 2 0 1 1 4 0v.1A1.6 1.6 0 0 0 17 4.6l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0 1.1 2.7H21a2 2 0 1 1 0 4h-.1a1.6 1.6 0 0 0-1.5 1z',
    ),
  ],

  // ---------- Dinero ----------
  banknote: [r(2, 6, 20, 12, 2), c(12, 12, 2.5), l(6, 12, 6.01, 12), l(18, 12, 18.01, 12)],
  'send-money': [p('M22 2 11 13'), p('M22 2 15 22l-4-9-9-4z')],
  scale: [
    l(12, 3, 12, 21),
    p('M5 7h14'),
    p('M7 7 4 14h6z'),
    p('M17 7l-3 7h6z'),
    p('M8 21h8'),
  ],
  truck: [r(1, 6, 13, 10, 2), p('M14 9h4l3 3.5V16h-7z'), c(6, 18, 1.8), c(17.5, 18, 1.8)],
  tag: [p('M20 12.5 12.5 20a2 2 0 0 1-2.8 0L3 13.3V4h9.3l7.7 7.7a1.6 1.6 0 0 1 0 .8z'), c(8, 8, 1.4)],
  bank: [pl('3 10 12 4 21 10'), l(4, 10, 4, 19), l(9, 10, 9, 19), l(15, 10, 15, 19), l(20, 10, 20, 19), l(2, 21, 22, 21)],
  book: [p('M4 4.5A2.5 2.5 0 0 1 6.5 2H20v18H6.5A2.5 2.5 0 0 0 4 22.5z'), l(8, 7, 16, 7)],

  // ---------- Acciones ----------
  check: [pl('4 12.5 9.5 18 20 6.5')],
  'check-circle': [c(12, 12, 9), pl('8 12.2 11 15.2 16 9.5')],
  x: [l(5.5, 5.5, 18.5, 18.5), l(18.5, 5.5, 5.5, 18.5)],
  'x-circle': [c(12, 12, 9), l(9, 9, 15, 15), l(15, 9, 9, 15)],
  plus: [l(12, 5, 12, 19), l(5, 12, 19, 12)],
  trash: [pl('3 6 21 6'), p('M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2'), p('M6 6v14a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V6')],
  pencil: [p('M17 3.5a2.1 2.1 0 0 1 3 3L8 18.5 3 20l1.5-5z')],
  eye: [p('M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7z'), c(12, 12, 3)],
  'eye-off': [p('M10.6 6.2A9.7 9.7 0 0 1 12 6c6.4 0 10 6 10 6a17 17 0 0 1-3.3 3.9'), p('M6.3 7.5A17 17 0 0 0 2 12s3.6 6 10 6a9.6 9.6 0 0 0 4-.8'), l(3, 3, 21, 21)],
  lock: [r(4, 11, 16, 10, 2), p('M8 11V8a4 4 0 0 1 8 0v3')],
  unlock: [r(4, 11, 16, 10, 2), p('M8 11V8a4 4 0 0 1 7.5-2')],
  key: [c(8, 15, 4), p('M11 12 21 2'), p('M18 5l2 2'), p('M15 8l2 2')],
  refresh: [p('M21 12a9 9 0 1 1-2.6-6.4'), pl('21 3 21 9 15 9')],
  shuffle: [pl('16 3 21 3 21 8'), p('M4 20 21 3'), pl('21 16 21 21 16 21'), p('M15 15l6 6'), p('M4 4l5 5')],
  'log-out': [p('M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4'), pl('16 17 21 12 16 7'), l(21, 12, 9, 12)],
  menu: [l(3, 6, 21, 6), l(3, 12, 21, 12), l(3, 18, 21, 18)],
  link: [p('M10 13a5 5 0 0 0 7.5.6l3-3a5 5 0 0 0-7-7l-1.7 1.7'), p('M14 11a5 5 0 0 0-7.5-.6l-3 3a5 5 0 0 0 7 7l1.7-1.7')],
  download: [p('M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4'), pl('7 10 12 15 17 10'), l(12, 15, 12, 3)],
  upload: [p('M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4'), pl('17 8 12 3 7 8'), l(12, 3, 12, 15)],

  // ---------- Flechas ----------
  'arrow-right': [l(4, 12, 19, 12), pl('13 6 19 12 13 18')],
  'arrow-left': [l(20, 12, 5, 12), pl('11 6 5 12 11 18')],
  'arrow-up-right': [l(6, 18, 18, 6), pl('9 6 18 6 18 15')],
  'chevron-right': [pl('9 5 16 12 9 19')],
  'chevron-down': [pl('5 9 12 16 19 9')],
  'chevron-up': [pl('5 15 12 8 19 15')],
  /** Indicador de columna ordenable que todavía no ordena. */
  'chevron-up-down': [pl('8 10 12 6 16 10'), pl('8 14 12 18 16 14')],

  // ---------- Estado e información ----------
  'alert-triangle': [p('M12 3.5 22 20H2z'), l(12, 10, 12, 14.5), l(12, 17, 12, 17.1)],
  info: [c(12, 12, 9), l(12, 11, 12, 16.5), l(12, 7.8, 12, 7.9)],
  ban: [c(12, 12, 9), l(5.6, 5.6, 18.4, 18.4)],
  siren: [p('M6 20v-6a6 6 0 0 1 12 0v6'), r(4, 20, 16, 2, 1), l(12, 3, 12, 5), l(5, 6, 6.5, 7.5), l(19, 6, 17.5, 7.5)],
  clock: [c(12, 12, 9), pl('12 7 12 12 16 14')],
  calendar: [r(3, 5, 18, 16, 2), l(3, 10, 21, 10), l(8, 3, 8, 6), l(16, 3, 16, 6)],
  inbox: [p('M22 12h-6l-2 3h-4l-2-3H2'), p('M5.5 5h13l3.5 7v7a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-7z')],
  'shopping-cart': [c(9, 20, 1.6), c(18, 20, 1.6), p('M2 3h3l2.7 12.4a1.5 1.5 0 0 0 1.5 1.2h8.5a1.5 1.5 0 0 0 1.5-1.2L21 7H6')],
  flag: [p('M5 21V4s2-1 5-1 5 2 8 2 3-1 3-1v9s-1 1-3 1-5-2-8-2-5 1-5 1z')],
  award: [c(12, 9, 6), pl('8.5 14 7 22 12 19 17 22 15.5 14')],
  handshake: [p('M11 17 8.5 14.5a2 2 0 0 1 0-2.8L13 7l3 3 5-5'), p('M3 8l4-4 4 4'), p('M13 17l2 2a1.5 1.5 0 0 0 2-2')],
  megaphone: [p('M3 11v2a2 2 0 0 0 2 2h1l8 5V4L6 9H5a2 2 0 0 0-2 2z'), p('M18 9a4 4 0 0 1 0 6')],
  mail: [r(2, 5, 20, 14, 2), pl('2.5 6.5 12 13 21.5 6.5')],
  message: [p('M21 15a2 2 0 0 1-2 2H8l-5 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z')],
  smartphone: [r(6, 2, 12, 20, 2), l(10.5, 18.5, 13.5, 18.5)],
  bolt: [c(12, 12, 3.2), p('M12 4v3'), p('M12 17v3'), p('M4 12h3'), p('M17 12h3')],
  'trending-up': [pl('2 17 9 10 13 14 22 5'), pl('16 5 22 5 22 11')],
  'trending-down': [pl('2 7 9 14 13 10 22 19'), pl('16 19 22 19 22 13')],
  'bar-chart': [l(12, 20, 12, 10), l(18, 20, 18, 4), l(6, 20, 6, 14), l(3, 21, 21, 21)],
  sparkle: [p('M12 3l1.8 4.9L19 9.7l-5.2 1.8L12 16.4l-1.8-4.9L5 9.7l5.2-1.8z'), p('M18.5 16.5l.8 2 2 .8-2 .8-.8 2-.8-2-2-.8 2-.8z')],
  ticket: [p('M3 8a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2a2 2 0 0 0 0 4v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2a2 2 0 0 0 0-4z'), l(13, 6, 13, 18)],
  dot: [c(12, 12, 4)],
};

/** Icono por defecto cuando el nombre no existe: evita un hueco silencioso. */
export const FALLBACK_ICON = 'dot';
